interface JwtPayload {
  exp?: number;
}

const SESSION_EXPIRED_MESSAGE = 'Η συνεδρία έληξε. Συνδεθείτε ξανά.';
const NETWORK_ERROR_MESSAGE = 'Αποτυχία σύνδεσης με τον server. Παρακαλώ δοκιμάστε ξανά.';

function parseJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(base64);
    return JSON.parse(decoded) as JwtPayload;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string, skewSeconds = 30): boolean {
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return false;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSeconds + skewSeconds;
}

export function clearSession(): void {
  localStorage.removeItem('user');
}

export function getStoredUser(): any | null {
  const userRaw = localStorage.getItem('user');
  if (!userRaw) return null;

  try {
    const user = JSON.parse(userRaw);
    const token = user?.accessToken;
    if (token && isTokenExpired(token)) {
      clearSession();
      return null;
    }
    return user;
  } catch {
    clearSession();
    return null;
  }
}

export function getAuthHeader(): HeadersInit {
  const user = getStoredUser();
  if (user?.accessToken) {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${user.accessToken}`
    };
  }

  return { 'Content-Type': 'application/json' };
}

function redirectToLoginIfNeeded(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname !== '/login') {
    window.location.href = '/login?session=expired';
  }
}

async function parseError(response: Response): Promise<string> {
  const text = await response.text();
  if (!text) return `Request failed (${response.status})`;

  try {
    const data = JSON.parse(text);
    return data?.message || data?.error || `Request failed (${response.status})`;
  } catch {
    return text;
  }
}

export async function fetchWithAuth(url: string, options: RequestInit = {}, requireAuth = true): Promise<Response> {
  const headers: HeadersInit = {
    ...(getAuthHeader() || {}),
    ...(options.headers || {})
  };

  const token = (getStoredUser() as any)?.accessToken;
  if (requireAuth && !token) {
    clearSession();
    redirectToLoginIfNeeded();
    throw new Error(SESSION_EXPIRED_MESSAGE);
  }

  let response: Response;
  try {
    response = await fetch(url, { ...options, headers });
  } catch {
    throw new Error(NETWORK_ERROR_MESSAGE);
  }

  if (response.status === 401 || response.status === 403) {
    clearSession();
    redirectToLoginIfNeeded();
    throw new Error(SESSION_EXPIRED_MESSAGE);
  }

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  return response;
}

